import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { BookPlus, CalendarPlus, Pencil, Plus, Send, Trash2 } from "lucide-react";

import {
  libraryService,
  type Author,
  type Book,
  type BookCategory,
  type BookCopy,
  type BookInput,
  type Borrowing,
  type EligibleStudent,
  type Reservation,
} from "../../services/libraryService";
import { apiErrorMessage } from "../../utils/apiError";
import { ConfirmDialog, type ConfirmRequest } from "../ConfirmDialog";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { DetailsModal } from "../register/DetailsModal";
import { Notice, deleteErrorText, type NoticeState } from "../register/RecordParts";
import { IssueForm, ReserveForm } from "./CirculationForms";
import {
  CONDITIONS,
  bookConfig,
  bookStatus,
  copyConfig,
  copyStatus,
  day,
  isOpenReservation,
  isOut,
  labelFrom,
  loanStatus,
  money,
  nextCopyNumber,
  shelfPlace,
} from "./libraryLogic";

const bookColumns: RegisterColumn<Book>[] = [
  {
    header: "Title",
    cell: (row) => (
      <span className="block">
        <span className="font-medium text-gray-900">{row.title}</span>
        {row.subtitle && <span className="block text-xs text-gray-500">{row.subtitle}</span>}
      </span>
    ),
    exportValue: (row) => [row.title, row.subtitle].filter(Boolean).join(": "),
    className: "min-w-[14rem] whitespace-normal",
  },
  { header: "Author", cell: (row) => row.authors_display || <span className="text-gray-300">Unknown</span>, exportValue: (row) => row.authors_display, className: "min-w-[9rem] whitespace-normal text-gray-700" },
  { header: "Category", cell: (row) => row.category_name || <span className="text-gray-300">—</span>, exportValue: (row) => row.category_name ?? "", className: "text-gray-700" },
  { header: "Shelf", cell: (row) => row.shelf_number || <span className="text-gray-300">—</span>, exportValue: (row) => shelfPlace(row), className: "text-gray-700" },
  { header: "ISBN", cell: (row) => row.isbn || <span className="text-gray-300">—</span>, exportValue: (row) => row.isbn ?? "", className: "font-mono text-xs text-gray-600" },
  { header: "Copies", cell: (row) => row.total_copies, exportValue: (row) => row.total_copies, align: "right" },
  { header: "Availability", cell: (row) => <StatusDot {...bookStatus(row)} />, exportValue: (row) => bookStatus(row).label },
];

const copyColumns: RegisterColumn<BookCopy>[] = [
  { header: "Title", cell: (row) => <span className="font-medium text-gray-900">{row.book_title}</span>, exportValue: (row) => row.book_title, className: "min-w-[14rem] whitespace-normal" },
  { header: "Copy", cell: (row) => row.copy_number, exportValue: (row) => row.copy_number, className: "tabular-nums text-gray-700" },
  { header: "Barcode", cell: (row) => row.barcode || <span className="text-gray-300">—</span>, exportValue: (row) => row.barcode ?? "", className: "font-mono text-xs text-gray-600" },
  { header: "Where", cell: (row) => row.location || <span className="text-gray-300">—</span>, exportValue: (row) => row.location ?? "", className: "text-gray-700" },
  { header: "Condition", cell: (row) => labelFrom(CONDITIONS, row.condition), exportValue: (row) => labelFrom(CONDITIONS, row.condition), className: "text-gray-700" },
  { header: "Status", cell: (row) => <StatusDot {...copyStatus(row)} />, exportValue: (row) => copyStatus(row).label },
];

/**
 * The catalogue: every title, where it sits, and how many copies are on the
 * shelf. Staff add and correct titles and copies and lend from here; pupils
 * and parents find a book and reserve it.
 */
export function CataloguePanel({ books, copies, loans, reservations, authors, categories, students, isLoading, isError, onRetry, canManage, canReserve, onChanged, openId }: {
  /** A title to open straight away, e.g. one picked from the copies list. */
  openId?: number | null;
  books?: Book[];
  copies: BookCopy[];
  loans: Borrowing[];
  reservations: Reservation[];
  authors: Author[];
  categories: BookCategory[];
  students: EligibleStudent[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  canManage: boolean;
  canReserve: boolean;
  onChanged: () => void;
}) {
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(openId ?? null);
  const [editing, setEditing] = useState<{ record?: Book } | null>(null);
  const [copyForm, setCopyForm] = useState<{ book: Book; record?: BookCopy } | null>(null);
  const [issuing, setIssuing] = useState<number | null>(null);
  const [reserving, setReserving] = useState<number | null>(null);
  const [dialog, setDialog] = useState<ConfirmRequest | null>(null);
  const done = (text: string) => {
    setNotice({ tone: "success", text });
    onChanged();
  };
  const remove = useMutation({
    mutationFn: ({ kind, id }: { kind: "book" | "copy"; id: number }) => (kind === "book" ? libraryService.deleteBook(id) : libraryService.deleteCopy(id)),
    onSuccess: (_result, variables) => {
      setDialog(null);
      if (variables.kind === "book") setViewing(null);
      done(variables.kind === "book" ? "Title removed from the catalogue." : "Copy removed.");
    },
    onError: (error) => {
      setDialog(null);
      setNotice({ tone: "error", text: deleteErrorText(error) });
    },
  });

  const book = viewing !== null && !editing && !copyForm && issuing === null && reserving === null && !dialog ? books?.find((item) => item.id === viewing) : undefined;
  const ownCopies = book ? copies.filter((copy) => copy.book === book.id) : [];
  const queue = book ? reservations.filter((row) => row.book === book.id && isOpenReservation(row)).sort((a, b) => a.priority - b.priority) : [];
  const lentCopies = new Set(loans.map((loan) => loan.book_copy));
  const nobodyHasBorrowed = (copy: BookCopy) => !lentCopies.has(copy.id);

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        title="Catalogue"
        description={canManage ? "Every title the library holds. Open one to see its copies, lend it, or correct its record." : "Find a book and see whether a copy is on the shelf. Open it to reserve."}
        actions={
          <>
            {canReserve && (
              <button type="button" disabled={!books?.length} onClick={() => setReserving(0)} className="btn btn-outline h-8 min-h-8 gap-1.5 px-3 text-xs disabled:opacity-50">
                <CalendarPlus className="h-3.5 w-3.5" /> Reserve a book
              </button>
            )}
            {canManage && (
              <button type="button" onClick={() => setEditing({})} className="btn-primary h-8 min-h-8 px-3 text-xs">
                <BookPlus className="mr-1.5 h-3.5 w-3.5" /> New title
              </button>
            )}
          </>
        }
        rows={books}
        config={bookConfig}
        columns={bookColumns}
        rowKey={(row) => row.id}
        onOpen={(row) => setViewing(row.id)}
        noun={["title", "titles"]}
        exportName={{ file: "library-catalogue", title: "Library Catalogue" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText={canManage ? "The catalogue is empty. Add the first title." : "The library has not catalogued any books yet."}
      />

      {book && (
        <DetailsModal
          size="3xl"
          title={book.title}
          headline={{ label: "On the shelf", value: `${book.available_copies} of ${book.total_copies}`, note: book.authors_display || "Author not recorded" }}
          status={bookStatus(book)}
          facts={[
            ["Author", book.authors_display],
            ["Category", book.category_name],
            ["Where", shelfPlace(book) || "Not recorded"],
            ["ISBN", book.isbn],
            ["Edition", book.edition],
            ["Published", book.publication_year ? String(book.publication_year) : undefined],
            ["Pages", book.pages ? String(book.pages) : undefined],
            ["Language", book.language],
            ...(canManage
              ? ([
                  ["Acquired", [day(book.acquisition_date), book.source].filter(Boolean).join(" · ") || undefined],
                  ["Price", book.acquisition_price ? money(book.acquisition_price) : undefined],
                  ["Waiting to borrow", String(queue.length)],
                ] as [string, string | undefined][])
              : []),
          ]}
          sections={[
            { title: "About the book", body: book.description },
            { title: "Tags", body: book.tags },
            ...(canManage ? [{ title: "Notes", body: book.notes }] : []),
          ]}
          onClose={() => setViewing(null)}
          footer={
            <>
              {canManage && book.total_copies === 0 && (
                <button
                  type="button"
                  onClick={() => setDialog({
                    title: "Remove title?",
                    body: <p><strong>{book.title}</strong> will be removed from the catalogue. It has no copies, and this cannot be undone.</p>,
                    confirmLabel: "Remove title",
                    tone: "danger",
                    onConfirm: () => remove.mutate({ kind: "book", id: book.id }),
                  })}
                  className="mr-auto inline-flex items-center gap-1.5 rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" /> Remove
                </button>
              )}
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              {canReserve && (
                <button type="button" className="btn btn-outline gap-1.5" onClick={() => setReserving(book.id)}>
                  <CalendarPlus className="h-4 w-4" /> Reserve
                </button>
              )}
              {canManage && (
                <>
                  <button type="button" className="btn btn-outline gap-1.5" onClick={() => setEditing({ record: book })}>
                    <Pencil className="h-4 w-4" /> Edit
                  </button>
                  <button type="button" disabled={!book.available_copies} title={book.available_copies ? undefined : "No copy is on the shelf"} className="btn btn-primary gap-1.5" onClick={() => setIssuing(book.id)}>
                    <Send className="h-4 w-4" /> Lend a copy
                  </button>
                </>
              )}
            </>
          }
        >
          <section>
            <div className="mb-2 flex items-center justify-between">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Copies</h4>
              {canManage && (
                <button type="button" onClick={() => setCopyForm({ book })} className="inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:underline">
                  <Plus className="h-3.5 w-3.5" /> Add a copy
                </button>
              )}
            </div>
            {ownCopies.length ? (
              <ol className="divide-y divide-gray-100 rounded-lg border border-gray-200">
                {ownCopies.map((copy) => {
                  const loan = loans.find((item) => item.book_copy === copy.id && isOut(item));
                  return (
                    <li key={copy.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 text-sm">
                      <span className="min-w-[8rem] font-medium text-gray-900">Copy {copy.copy_number}</span>
                      <span className="text-xs text-gray-500">{[copy.barcode, copy.location, labelFrom(CONDITIONS, copy.condition)].filter(Boolean).join(" · ")}</span>
                      <span className="flex items-center gap-3">
                        {canManage && loan ? <StatusDot {...loanStatus(loan)} /> : <StatusDot {...copyStatus(copy)} />}
                        {canManage && loan && <span className="text-xs text-gray-500">{loan.borrower_name}</span>}
                        {canManage && (
                          <button type="button" onClick={() => setCopyForm({ book, record: copy })} className="text-xs font-medium text-primary-700 hover:underline">Edit</button>
                        )}
                        {canManage && nobodyHasBorrowed(copy) && (
                          <button
                            type="button"
                            onClick={() => setDialog({
                              title: "Remove copy?",
                              body: <p>Copy <strong>{copy.copy_number}</strong> of {book.title} has never been lent, so it can be removed. This cannot be undone.</p>,
                              confirmLabel: "Remove copy",
                              tone: "danger",
                              onConfirm: () => remove.mutate({ kind: "copy", id: copy.id }),
                            })}
                            className="text-xs font-medium text-red-700 hover:underline"
                          >
                            Remove
                          </button>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ol>
            ) : (
              <p className="text-sm text-gray-500">No copies yet{canManage ? ". Add one so it can be lent." : "."}</p>
            )}
          </section>
          {canManage && queue.length > 0 && (
            <section>
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Waiting for it</h4>
              <ol className="divide-y divide-gray-100 rounded-lg border border-gray-200">
                {queue.map((row) => (
                  <li key={row.id} className="flex justify-between gap-2 px-4 py-2 text-sm">
                    <span>{row.priority}. {row.student_name}</span>
                    <span className="text-xs text-gray-500">until {day(row.expiry_date)}</span>
                  </li>
                ))}
              </ol>
            </section>
          )}
        </DetailsModal>
      )}

      {editing && (
        <BookForm
          record={editing.record}
          authors={authors}
          categories={categories}
          onClose={() => setEditing(null)}
          onDone={(saved, created) => {
            setEditing(null);
            setViewing(saved.id);
            done(created ? `${saved.title} added${saved.total_copies ? ` with ${saved.total_copies} ${saved.total_copies === 1 ? "copy" : "copies"}` : ""}.` : `${saved.title} saved.`);
          }}
        />
      )}
      {copyForm && (
        <CopyForm
          book={copyForm.book}
          record={copyForm.record}
          siblings={copies.filter((copy) => copy.book === copyForm.book.id)}
          onClose={() => setCopyForm(null)}
          onDone={(text) => {
            setCopyForm(null);
            done(text);
          }}
        />
      )}
      {issuing !== null && (
        <IssueForm
          students={students}
          copies={copies}
          bookId={issuing || undefined}
          onClose={() => setIssuing(null)}
          onDone={(text) => {
            setIssuing(null);
            done(text);
          }}
        />
      )}
      {reserving !== null && (
        <ReserveForm
          students={students}
          books={books ?? []}
          bookId={reserving || undefined}
          onClose={() => setReserving(null)}
          onDone={(text) => {
            setReserving(null);
            setViewing(null);
            done(text);
          }}
        />
      )}
      <ConfirmDialog request={dialog} onClose={() => setDialog(null)} busy={remove.isPending} />
    </div>
  );
}

/** Every copy on its own line: find one by barcode, see what is out, lost or being repaired. */
export function CopiesPanel({ copies, isLoading, onOpenTitle }: {
  copies?: BookCopy[];
  isLoading: boolean;
  onOpenTitle: (bookId: number) => void;
}) {
  return (
    <ClientRegister
      title="Copies"
      description="Each physical book. Open one to go to its title."
      rows={copies}
      config={copyConfig}
      columns={copyColumns}
      rowKey={(row) => row.id}
      onOpen={(row) => onOpenTitle(row.book)}
      openLabel={() => "Title"}
      noun={["copy", "copies"]}
      exportName={{ file: "library-copies", title: "Library Copies" }}
      isLoading={isLoading}
      emptyText="No copies yet. Add them from a title in the catalogue."
    />
  );
}

const text = (data: FormData, name: string) => String(data.get(name) ?? "").trim();
const numberOrNull = (data: FormData, name: string) => (text(data, name) ? Number(text(data, name)) : null);

function BookForm({ record, authors, categories, onClose, onDone }: {
  record?: Book;
  authors: Author[];
  categories: BookCategory[];
  onClose: () => void;
  onDone: (book: Book, created: boolean) => void;
}) {
  const [chosenAuthors, setChosenAuthors] = useState<number[]>(record?.authors ?? []);
  const [authorQuery, setAuthorQuery] = useState("");
  const [extraAuthors, setExtraAuthors] = useState<Author[]>([]);
  const [newAuthor, setNewAuthor] = useState({ first: "", last: "" });
  const [category, setCategory] = useState(record?.category ? String(record.category) : "");
  const [extraCategories, setExtraCategories] = useState<BookCategory[]>([]);
  const [newCategory, setNewCategory] = useState("");
  const allAuthors = [...authors, ...extraAuthors];
  const allCategories = [...categories, ...extraCategories];
  const shownAuthors = allAuthors.filter((author) => chosenAuthors.includes(author.id) || author.full_name.toLowerCase().includes(authorQuery.toLowerCase()));

  const addAuthor = useMutation({
    mutationFn: () => libraryService.createAuthor({ first_name: newAuthor.first.trim(), last_name: newAuthor.last.trim() }),
    onSuccess: (author) => {
      setExtraAuthors((list) => [...list, author]);
      setChosenAuthors((list) => [...list, author.id]);
      setNewAuthor({ first: "", last: "" });
    },
  });
  const addCategory = useMutation({
    mutationFn: () => libraryService.createCategory({ name: newCategory.trim(), color: "#3B82F6" }),
    onSuccess: (created) => {
      setExtraCategories((list) => [...list, created]);
      setCategory(String(created.id));
      setNewCategory("");
    },
  });
  const save = useMutation({
    mutationFn: async ({ data, copiesToAdd }: { data: BookInput; copiesToAdd: number }) => {
      const saved = record ? await libraryService.updateBook(record.id, data) : await libraryService.createBook({ ...data, status: "available" });
      for (let index = 1; index <= copiesToAdd; index += 1) {
        await libraryService.createCopy({ book: saved.id, copy_number: String(index).padStart(3, "0"), condition: "good" });
      }
      return { ...saved, total_copies: saved.total_copies + copiesToAdd, available_copies: saved.available_copies + copiesToAdd };
    },
    onSuccess: (saved) => onDone(saved, !record),
  });

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    save.mutate({
      copiesToAdd: record ? 0 : Math.max(0, Math.min(50, Number(text(data, "copies")) || 0)),
      data: {
        title: text(data, "title"),
        subtitle: text(data, "subtitle") || null,
        authors: chosenAuthors,
        category: Number(category) || null,
        isbn: text(data, "isbn") || null,
        edition: text(data, "edition") || null,
        publication_year: numberOrNull(data, "publication_year"),
        pages: numberOrNull(data, "pages"),
        language: text(data, "language") || "English",
        shelf_number: text(data, "shelf_number") || null,
        row_number: text(data, "row_number") || null,
        position: text(data, "position") || null,
        acquisition_date: text(data, "acquisition_date") || null,
        acquisition_price: text(data, "acquisition_price") || null,
        source: text(data, "source") || null,
        tags: text(data, "tags") || null,
        description: text(data, "description") || null,
        notes: text(data, "notes") || null,
      },
    });
  };

  return (
    <FormModal
      id="book-form"
      title={record ? `Edit ${record.title}` : "New title"}
      busy={save.isPending}
      error={save.isError ? apiErrorMessage(save.error, "The title could not be saved.") : undefined}
      saveLabel={record ? "Save changes" : "Add title"}
      note={record ? "Copies are managed from the title's details." : "A title is the book; copies are the ones on the shelf."}
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="The book" divided={false} columns={2}>
        <Field label="Title" className="sm:col-span-2">
          <input required autoFocus name="title" defaultValue={record?.title} className="input mt-1" placeholder="e.g. Mathematics for Primary Schools, Pupil's Book 5" />
        </Field>
        <Field label="Subtitle" optional className="sm:col-span-2">
          <input name="subtitle" defaultValue={record?.subtitle ?? ""} className="input mt-1" />
        </Field>
        <Field as="div" label="Authors" className="sm:col-span-2" hint={chosenAuthors.length ? `${chosenAuthors.length} chosen.` : "Choose at least one, or add a new author."}>
          <input type="search" value={authorQuery} onChange={(event) => setAuthorQuery(event.target.value)} placeholder="Find an author" className="input mt-1" />
          <div className="mt-2 flex max-h-32 flex-wrap gap-2 overflow-y-auto">
            {shownAuthors.map((author) => (
              <label key={author.id} className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-gray-200 px-2.5 py-1 text-xs text-gray-700 has-[:checked]:border-primary-500 has-[:checked]:bg-primary-50">
                <input
                  type="checkbox"
                  checked={chosenAuthors.includes(author.id)}
                  onChange={() => setChosenAuthors((list) => (list.includes(author.id) ? list.filter((id) => id !== author.id) : [...list, author.id]))}
                  className="h-3.5 w-3.5 accent-primary-600"
                />
                {author.full_name}
              </label>
            ))}
            {!shownAuthors.length && <span className="text-xs text-gray-400">No author matches.</span>}
          </div>
          <div className="mt-2 grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
            <input aria-label="New author's first name" value={newAuthor.first} onChange={(event) => setNewAuthor((value) => ({ ...value, first: event.target.value }))} placeholder="New author: first name" className="input" />
            <input aria-label="New author's surname" value={newAuthor.last} onChange={(event) => setNewAuthor((value) => ({ ...value, last: event.target.value }))} placeholder="Surname" className="input" />
            <button type="button" disabled={!newAuthor.first.trim() || !newAuthor.last.trim() || addAuthor.isPending} onClick={() => addAuthor.mutate()} className="btn btn-outline">Add author</button>
          </div>
          {addAuthor.isError && <p className="mt-1 text-xs text-red-700">{apiErrorMessage(addAuthor.error, "The author could not be added.")}</p>}
        </Field>
        <Field as="div" label="Category" optional>
          <select value={category} onChange={(event) => setCategory(event.target.value)} className="input mt-1">
            <option value="">Uncategorised</option>
            {allCategories.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
          </select>
          <div className="mt-2 flex gap-2">
            <input aria-label="New category" value={newCategory} onChange={(event) => setNewCategory(event.target.value)} placeholder="Or a new category" className="input" />
            <button type="button" disabled={!newCategory.trim() || addCategory.isPending} onClick={() => addCategory.mutate()} className="btn btn-outline">Add</button>
          </div>
          {addCategory.isError && <p className="mt-1 text-xs text-red-700">{apiErrorMessage(addCategory.error, "The category could not be added.")}</p>}
        </Field>
        <Field label="ISBN" optional>
          <input name="isbn" defaultValue={record?.isbn ?? ""} className="input mt-1 font-mono" placeholder="e.g. 9789970021234" />
        </Field>
        <Field label="Edition" optional>
          <input name="edition" defaultValue={record?.edition ?? ""} className="input mt-1" placeholder="e.g. 2nd" />
        </Field>
        <Field label="Year published" optional>
          <input type="number" min={1800} max={2100} name="publication_year" defaultValue={record?.publication_year ?? ""} className="input mt-1 tabular-nums" />
        </Field>
        <Field label="Pages" optional>
          <input type="number" min={1} name="pages" defaultValue={record?.pages ?? ""} className="input mt-1 tabular-nums" />
        </Field>
        <Field label="Language">
          <input name="language" defaultValue={record?.language ?? "English"} className="input mt-1" />
        </Field>
      </FormSection>

      <FormSection title="Where it sits" description="So anyone can find it on the shelves." columns={3}>
        <Field label="Shelf" optional>
          <input name="shelf_number" defaultValue={record?.shelf_number ?? ""} className="input mt-1" placeholder="e.g. B4" />
        </Field>
        <Field label="Row" optional>
          <input name="row_number" defaultValue={record?.row_number ?? ""} className="input mt-1" />
        </Field>
        <Field label="Position" optional>
          <input name="position" defaultValue={record?.position ?? ""} className="input mt-1" />
        </Field>
      </FormSection>

      <FormSection title="How the library got it" columns={3}>
        <Field label="Date acquired" optional>
          <input type="date" name="acquisition_date" defaultValue={record?.acquisition_date ?? ""} className="input mt-1" />
        </Field>
        <Field label="Price (UGX)" optional>
          <input type="number" min={0} step="any" name="acquisition_price" defaultValue={record?.acquisition_price ? Number(record.acquisition_price) : ""} className="input mt-1 tabular-nums" />
        </Field>
        <Field label="From" optional hint="Bought, donated by…">
          <input name="source" defaultValue={record?.source ?? ""} className="input mt-1" />
        </Field>
      </FormSection>

      <FormSection title="About it" columns={1}>
        <Field label="Description" optional hint="What the book is about; readers see this.">
          <textarea name="description" rows={3} defaultValue={record?.description ?? ""} className="input mt-1" />
        </Field>
        <Field label="Tags" optional hint="Comma-separated, e.g. P5, mathematics, revision.">
          <input name="tags" defaultValue={record?.tags ?? ""} className="input mt-1" />
        </Field>
        <Field label="Notes for library staff" optional>
          <textarea name="notes" rows={2} defaultValue={record?.notes ?? ""} className="input mt-1" />
        </Field>
        {!record && (
          <Field label="Copies to add now" hint="Numbered 001, 002 and so on. Barcodes can be added to each copy later.">
            <input type="number" min={0} max={50} name="copies" defaultValue={1} className="input mt-1 tabular-nums sm:max-w-[8rem]" />
          </Field>
        )}
      </FormSection>
    </FormModal>
  );
}

function CopyForm({ book, record, siblings, onClose, onDone }: {
  book: Book;
  record?: BookCopy;
  siblings: BookCopy[];
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const out = record && (record.status === "borrowed" || record.status === "reserved");
  const save = useMutation({
    mutationFn: (data: Partial<BookCopy>) => (record ? libraryService.updateCopy(record.id, data) : libraryService.createCopy({ ...data, book: book.id })),
    onSuccess: (copy) => onDone(record ? `Copy ${copy.copy_number} saved.` : `Copy ${copy.copy_number} of ${book.title} added.`),
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const changes: Partial<BookCopy> = { barcode: text(data, "barcode") || null, location: text(data, "location") || null, notes: text(data, "notes") || null };
    // A copy that is out keeps its number and condition until it comes back.
    if (!out) Object.assign(changes, { copy_number: text(data, "copy_number"), condition: text(data, "condition") });
    save.mutate(changes);
  };
  return (
    <FormModal
      id="copy-form"
      title={record ? `Edit copy ${record.copy_number}` : `Add a copy of ${book.title}`}
      busy={save.isPending}
      error={save.isError ? apiErrorMessage(save.error, "The copy could not be saved.") : undefined}
      saveLabel={record ? "Save changes" : "Add copy"}
      note={out ? "It is out, so only its barcode, place and notes can change now." : undefined}
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="Copy" divided={false} columns={2}>
        <Field label="Copy number">
          <input required name="copy_number" disabled={out} defaultValue={record?.copy_number ?? nextCopyNumber(siblings)} className="input mt-1 tabular-nums" />
        </Field>
        <Field label="Barcode" optional hint="Unique; scan it or type it.">
          <input name="barcode" defaultValue={record?.barcode ?? ""} className="input mt-1 font-mono" />
        </Field>
        <Field label="Condition">
          <select name="condition" disabled={out} defaultValue={record?.condition ?? "good"} className="input mt-1">
            {CONDITIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </Field>
        <Field label="Where it is kept" optional>
          <input name="location" defaultValue={record?.location ?? ""} className="input mt-1" placeholder="e.g. Reference shelf" />
        </Field>
        <Field label="Notes" optional className="sm:col-span-2">
          <textarea name="notes" rows={2} defaultValue={record?.notes ?? ""} className="input mt-1" />
        </Field>
      </FormSection>
    </FormModal>
  );
}
